/**
 * The save step's two controls for the TV and streaming formats (contract 1.13.0), shaped like
 * the Word layout control (lib/docx.ts): the words are the template's (`ui.stl_framerate` and
 * `ui.ttml_extension` in macros/ui.html), a page mounts each once, calls `sync` wherever its write
 * format changes, and asks it for what a write in that format needs.
 *
 * EBU STL's FRAME RATE. The file counts in frames and its header names the rate, so the writer
 * refuses without one. /srt-to-stl's copy promises "25 fps by default, 30 fps as an option", which
 * is this control: 25 checked, 30 the other choice. A page without the field writes 25.
 *
 * "30" IS COUNTED AT 29.97. ttconv (the independent reader the writer is checked against) reads an
 * `STL30.01` file as an NTSC timeline: frames at 30000/1001 a second, labelled drop-frame. So the
 * writer labels 29.97 drop-frame, as a 29.97 project shows it, and a drop-frame label keeps to the
 * clock: a project at exactly 30 fps reads the same labels within two frames. The writer still
 * takes an exact 30 from any caller that asks for one.
 *
 * TTML's EXTENSION. /srt-to-dfxp's copy says to save it "as .dfxp or .ttml, whichever the platform
 * asks for; the contents are the same". The writer makes one file (format `ttml`); this control
 * only renames the download. `.dfxp` is checked, the name the page is titled for.
 */

import type { Framerate, WriteFormat } from '../../../../../contracts/cue-model.ts';
import { on, qs, qsa } from './dom.ts';

export const STL_RATES: Readonly<Record<string, Framerate>> = {
  '25': { num: 25, den: 1, label: '25' },
  '30': { num: 30000, den: 1001, label: '29.97' },
};

/** The rate for a picked value: the 30 choice for exactly '30', 25 for anything else (the default). */
export function stlRateOf(value: string | null | undefined): Framerate {
  return value === '30' ? (STL_RATES['30'] as Framerate) : (STL_RATES['25'] as Framerate);
}

/** `.dfxp` unless the exact value 'ttml' was picked. */
export function ttmlExtensionOf(value: string | null | undefined): '.dfxp' | '.ttml' {
  return value === 'ttml' ? '.ttml' : '.dfxp';
}

/** `name.ttml` as `name.dfxp` (or the reverse): the TTML writer's file under the picked extension. */
export function renameTtml(filename: string, extension: '.dfxp' | '.ttml'): string {
  const dot = filename.lastIndexOf('.');
  return (dot > 0 ? filename.slice(0, dot) : filename) + extension;
}

export interface StlRateControl {
  sync(format: string | undefined): void;
  /** The rate a write in `format` sends: the picked one for EBU STL, null for any other format. */
  framerateFor(format: WriteFormat | string | undefined): Framerate | null;
}

export interface TtmlExtensionControl {
  sync(format: string | undefined): void;
  /** The download's name: renamed for a TTML write, unchanged for any other. */
  filenameFor(format: WriteFormat | string | undefined, filename: string): string;
}

function radios(
  root: ParentNode,
  field: string,
  input: string,
): {
  field: HTMLElement | null;
  picked: () => string | undefined;
  onChange: (fn: () => void) => void;
} {
  const host = qs<HTMLElement>(`[${field}]`, root);
  const inputs = host ? qsa<HTMLInputElement>(`[${input}]`, host) : [];
  return {
    field: host,
    picked: () => inputs.find((r) => r.checked)?.value,
    onChange: (fn) => {
      for (const r of inputs) {
        on(r, 'change', () => {
          if (r.checked) fn();
        });
      }
    },
  };
}

/** Mount the EBU STL rate. `onChange` should write the file again at the new rate. */
export function mountStlRate(root: ParentNode, onChange: () => void): StlRateControl {
  const control = radios(root, 'data-stl-rate-field', 'data-stl-rate');
  control.onChange(onChange);
  return {
    sync(format) {
      if (control.field) control.field.hidden = format !== 'ebu-stl';
    },
    framerateFor: (format) => (format === 'ebu-stl' ? stlRateOf(control.picked()) : null),
  };
}

/** Mount the TTML extension. Nothing is written again on a change: only the name differs. */
export function mountTtmlExtension(root: ParentNode): TtmlExtensionControl {
  const control = radios(root, 'data-ttml-extension-field', 'data-ttml-extension');
  return {
    sync(format) {
      if (control.field) control.field.hidden = format !== 'ttml';
    },
    filenameFor: (format, filename) =>
      format === 'ttml' ? renameTtml(filename, ttmlExtensionOf(control.picked())) : filename,
  };
}
